import "./delay-module.css";

import { Knob } from "./knob.ts";

// DelayModule — stereo delay with cross-feedback and tone shaping
// inside the feedback loop. Six knobs across two rows:
//
//   Row 1 (timing):    time L · time R · feedback
//   Row 2 (character): cross · tone · mix
//
// Engine paths and ranges:
//   fx.delay.time_l_ms        5..2000 ms (log)
//   fx.delay.time_r_ms        5..2000 ms (log)
//   fx.delay.feedback         0..0.95
//   fx.delay.cross_feedback   0..0.95
//   fx.delay.tone_hz          200..18000 Hz (log)
//   fx.delay.mix              0..1

export interface DelayState {
  timeLMs: number;
  timeRMs: number;
  feedback: number;
  crossFeedback: number;
  toneHz: number;
  mix: number;
}

export interface DelayModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  initial?: Partial<DelayState>;
}

export class DelayModule {
  private root: HTMLDivElement;
  private timeLKnob!: Knob;
  private timeRKnob!: Knob;
  private fbKnob!: Knob;
  private crossKnob!: Knob;
  private toneKnob!: Knob;
  private mixKnob!: Knob;

  constructor(opts: DelayModuleOptions) {
    const init = opts.initial ?? {};
    const timeL = init.timeLMs ?? 380;
    const timeR = init.timeRMs ?? 520;
    const fb = init.feedback ?? 0.35;
    const cross = init.crossFeedback ?? 0.15;
    const tone = init.toneHz ?? 6000;
    const mix = init.mix ?? 0;

    this.root = document.createElement("div");
    this.root.className = "delay-module";

    const header = document.createElement("div");
    header.className = "delay-module-header fx-module-header";
    const title = document.createElement("span");
    title.className = "delay-module-title fx-module-title";
    title.textContent = "delay";
    header.append(title);
    this.root.append(header);

    // Row 1 — timing.
    const row1 = document.createElement("div");
    row1.className = "delay-module-knobs";
    this.root.append(row1);
    this.timeLKnob = new Knob({
      container: row1,
      size: 40,
      min: 5,
      max: 2000,
      default: 380,
      value: timeL,
      taper: "log",
      label: "time L",
      format: formatMs,
      onChange: (v) => opts.setParam("fx.delay.time_l_ms", v),
    });
    this.timeRKnob = new Knob({
      container: row1,
      size: 40,
      min: 5,
      max: 2000,
      default: 520,
      value: timeR,
      taper: "log",
      label: "time R",
      format: formatMs,
      onChange: (v) => opts.setParam("fx.delay.time_r_ms", v),
    });
    this.fbKnob = new Knob({
      container: row1,
      size: 40,
      min: 0,
      max: 0.95,
      default: 0.35,
      value: fb,
      label: "fb",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.delay.feedback", v),
    });

    // Row 2 — character.
    const row2 = document.createElement("div");
    row2.className = "delay-module-knobs";
    this.root.append(row2);
    this.crossKnob = new Knob({
      container: row2,
      size: 40,
      min: 0,
      max: 0.95,
      default: 0.15,
      value: cross,
      label: "cross",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.delay.cross_feedback", v),
    });
    this.toneKnob = new Knob({
      container: row2,
      size: 40,
      min: 200,
      max: 18000,
      default: 6000,
      value: tone,
      taper: "log",
      label: "tone",
      format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`),
      onChange: (v) => opts.setParam("fx.delay.tone_hz", v),
    });
    this.mixKnob = new Knob({
      container: row2,
      size: 40,
      min: 0,
      max: 1,
      default: 0,
      value: mix,
      label: "mix",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.delay.mix", v),
    });

    opts.container.append(this.root);
  }

  getState(): DelayState {
    return {
      timeLMs: this.timeLKnob.current,
      timeRMs: this.timeRKnob.current,
      feedback: this.fbKnob.current,
      crossFeedback: this.crossKnob.current,
      toneHz: this.toneKnob.current,
      mix: this.mixKnob.current,
    };
  }

  setState(s: DelayState): void {
    this.timeLKnob.setValue(s.timeLMs);
    this.timeRKnob.setValue(s.timeRMs);
    this.fbKnob.setValue(s.feedback);
    this.crossKnob.setValue(s.crossFeedback);
    this.toneKnob.setValue(s.toneHz);
    this.mixKnob.setValue(s.mix);
  }

  destroy(): void {
    this.root.remove();
  }
}

function formatMs(v: number): string {
  if (v < 100) return `${v.toFixed(0)} ms`;
  if (v < 1000) return `${v.toFixed(0)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}
